import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "@/components/base/Badge";
import type { BadgeTone } from "@/components/base/Badge";
import {
  notifications as seed,
  notificationFilters,
  type Notification,
  type NotificationFilterKey,
  type NotificationType,
} from "@/mocks/notifications";

const typeMeta: Record<NotificationType, { icon: string; tone: BadgeTone; iconBg: string; iconColor: string }> = {
  order: {
    icon: "ri-shopping-bag-3-line",
    tone: "green",
    iconBg: "bg-[#E8F9F0]",
    iconColor: "text-[#27AE60]",
  },
  cancelled: {
    icon: "ri-close-circle-line",
    tone: "red",
    iconBg: "bg-[#FFF5F5]",
    iconColor: "text-[#E74C3C]",
  },
  review: {
    icon: "ri-star-line",
    tone: "blue",
    iconBg: "bg-[#EFF6FF]",
    iconColor: "text-[#1D4ED8]",
  },
  negative: {
    icon: "ri-emotion-unhappy-line",
    tone: "red",
    iconBg: "bg-[#FFF5F5]",
    iconColor: "text-[#E74C3C]",
  },
  settlement: {
    icon: "ri-bank-card-line",
    tone: "amber",
    iconBg: "bg-[#FFF9EB]",
    iconColor: "text-[#B45309]",
  },
  fine: {
    icon: "ri-alert-line",
    tone: "orange",
    iconBg: "bg-[#FFF5E6]",
    iconColor: "text-[#D97706]",
  },
};

export default function Notifications() {
  const [items, setItems] = useState<Notification[]>(seed);
  const [filter, setFilter] = useState<NotificationFilterKey>("all");

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "review") {
      return items.filter((n) => n.type === "review" || n.type === "negative");
    }
    return items.filter((n) => n.type === filter);
  }, [items, filter]);

  const unreadCount = items.filter((n) => !n.read).length;

  const markAsRead = (id: number) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const markAllAsRead = () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const countFor = (key: NotificationFilterKey) => {
    if (key === "all") return items.length;
    if (key === "review") {
      return items.filter((n) => n.type === "review" || n.type === "negative").length;
    }
    return items.filter((n) => n.type === key).length;
  };

  return (
    <div className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-[28px] font-bold text-ghasla-text-heading">الإشعارات</h1>
          {unreadCount > 0 && (
            <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-[#0D6E6E] px-2 text-xs font-bold text-white">
              {unreadCount}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={markAllAsRead}
          disabled={unreadCount === 0}
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl border border-[#E4EFEF] bg-white px-4 py-2.5 text-sm font-semibold text-ghasla-text-link transition-colors hover:bg-[#F0FDFD] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="ri-check-double-line text-lg" />
          تحديد الكل كمقروء
        </button>
      </div>

      {/* Filters */}
      <div className="mt-6 inline-flex flex-wrap items-center gap-1 rounded-full border border-[#E4EFEF] bg-white p-1">
        {notificationFilters.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active ? "bg-[#0D6E6E] text-white" : "text-ghasla-text-primary hover:bg-[#F0FDFD]"
              }`}
            >
              {f.label}
              <span className={`text-xs ${active ? "text-white/70" : "text-ghasla-text-secondary"}`}>
                {countFor(f.key)}
              </span>
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="mt-5 space-y-3">
        {filtered.map((n) => {
          const meta = typeMeta[n.type];
          return (
            <button
              key={n.id}
              type="button"
              onClick={() => markAsRead(n.id)}
              className={`flex w-full cursor-pointer items-start gap-4 rounded-[20px] p-5 text-right transition-colors ${
                n.read ? "bg-white shadow-card" : "bg-[#EFF8F8] shadow-card"
              }`}
            >
              <div
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${meta.iconBg}`}
              >
                <i className={`${meta.icon} text-xl ${meta.iconColor}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className={`text-sm ${n.read ? "font-medium text-ghasla-text-primary" : "font-bold text-ghasla-text-heading"}`}>
                    {n.title}
                  </p>
                  {!n.read && (
                    <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                  )}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-ghasla-text-primary">{n.message}</p>
                <p className="mt-2 text-xs text-ghasla-text-secondary">{n.time}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <Badge tone={meta.tone}>{meta.icon === "ri-star-line" || meta.icon === "ri-emotion-unhappy-line" ? "تقييم" : n.title}</Badge>
                {!n.read && (
                  <span className="text-xs font-medium text-ghasla-text-link">غير مقروء</span>
                )}
              </div>
            </button>
          );
        })}

        {filtered.length === 0 && (
          <div className="flex flex-col items-center gap-3 rounded-[20px] bg-white p-12 text-center shadow-card">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F0FDFD]">
              <i className="ri-notification-off-line text-2xl text-ghasla-text-secondary" />
            </div>
            <p className="text-sm font-medium text-ghasla-text-primary">لا توجد إشعارات في هذا التصنيف</p>
          </div>
        )}
      </div>

      {/* Settings hint */}
      <div className="mt-8 flex flex-col gap-3 rounded-[20px] bg-[#FFF9EB] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2 text-sm text-[#B45309]">
          <i className="ri-settings-3-line text-base" />
          تقدر تتحكم في نوعية الإشعارات اللي توصلك (Push / SMS) من إعدادات الإشعارات
        </p>
        <Link
          to="/provider/settings"
          className="inline-flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          إعدادات الإشعارات
          <i className="ri-arrow-left-line" />
        </Link>
      </div>
    </div>
  );
}